import React, { useState, useEffect } from 'react';
import { FaList, FaPlus, FaSave, FaUserShield, FaTrash, FaEdit, FaTimes, FaCheck } from 'react-icons/fa';
import { Oval } from 'react-loader-spinner';
import styles from './DataEntryValuesPage.module.css';
import { db } from '../config/firebaseConfig';
import { collection, writeBatch, doc, getDocs, query, orderBy, where } from 'firebase/firestore';
import { toast } from 'react-toastify';

const DataEntryValuesPage = () => {
  const [departments, setDepartments] = useState([]);
  const [authRoles, setAuthRoles] = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseSources, setExpenseSources] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isRolesSaving, setIsRolesSaving] = useState(false);
  const [isIncomeSourcesSaving, setIsIncomeSourcesSaving] = useState(false);
  const [isIncomeCategoriesSaving, setIsIncomeCategoriesSaving] = useState(false);
  const [isExpenseSourcesSaving, setIsExpenseSourcesSaving] = useState(false);
  const [isExpenseTypesSaving, setIsExpenseTypesSaving] = useState(false);
  const [isExpenseCategoriesSaving, setIsExpenseCategoriesSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRolesLoading, setIsRolesLoading] = useState(true);
  const [isIncomeSourcesLoading, setIsIncomeSourcesLoading] = useState(true);
  const [isIncomeCategoriesLoading, setIsIncomeCategoriesLoading] = useState(true);
  const [isExpenseSourcesLoading, setIsExpenseSourcesLoading] = useState(true);
  const [isExpenseTypesLoading, setIsExpenseTypesLoading] = useState(true);
  const [isExpenseCategoriesLoading, setIsExpenseCategoriesLoading] = useState(true);
  const [isCheckingReferences, setIsCheckingReferences] = useState(false);
  const [errors, setErrors] = useState({
    departments: {},
    roles: {},
    incomeSources: {},
    incomeCategories: {},
    expenseSources: {},
    expenseTypes: {},
    expenseCategories: {}
  });
  const [editingItems, setEditingItems] = useState({
    departments: {},
    roles: {},
    incomeSources: {},
    incomeCategories: {},
    expenseSources: {},
    expenseTypes: {},
    expenseCategories: {}
  });

  useEffect(() => {
    fetchDepartments();
    fetchAuthRoles();
    fetchIncomeSources();
    fetchIncomeCategories();
    fetchExpenseSources();
    fetchExpenseTypes();
    fetchExpenseCategories();
  }, []);

  const fetchDepartments = async () => {
    try {
      setIsLoading(true);
      const employeeDepartmentsRef = collection(db, 'DataEntryValues', 'departments', 'EmployeeDepartments');
      const q = query(employeeDepartmentsRef, orderBy('id', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedDepartments = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          value: data.value,
          isActive: data.isActive !== false // Default to true if not specified
        };
      });
      setDepartments(fetchedDepartments.length ? fetchedDepartments : [{ value: '', isActive: true }]);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Departmanlar yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAuthRoles = async () => {
    try {
      setIsRolesLoading(true);
      const authRolesRef = collection(db, 'DataEntryValues', 'roles', 'AuthorizationRoles');
      const q = query(authRolesRef, orderBy('id', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedRoles = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          value: data.value,
          isActive: data.isActive !== false // Default to true if not specified
        };
      });
      
      // If no roles exist yet, initialize with the default values
      if (fetchedRoles.length === 0) {
        setAuthRoles([
          { value: 'Finans Yöneticisi', isActive: true }, 
          { value: 'Proje Yöneticisi', isActive: true }, 
          { value: 'Saha Koordinatörü', isActive: true }, 
          { value: 'İnsan Kaynakları', isActive: true },
          { value: 'SMO Lead', isActive: true }
        ]);
      } else {
        setAuthRoles(fetchedRoles);
      }
    } catch (error) {
      console.error('Error fetching authorization roles:', error);
      toast.error('Yetkilendirme rolleri yüklenirken bir hata oluştu.');
    } finally {
      setIsRolesLoading(false);
    }
  };

  const fetchIncomeSources = async () => {
    try {
      setIsIncomeSourcesLoading(true);
      const incomeSourcesRef = collection(db, 'DataEntryValues', 'income', 'IncomeSources');
      const q = query(incomeSourcesRef, orderBy('id', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedSources = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          value: data.value,
          isActive: data.isActive !== false
        };
      });
      setIncomeSources(fetchedSources.length ? fetchedSources : [
        { value: 'Satışlar', isActive: true },
        { value: 'Hizmetler', isActive: true },
        { value: 'Yatırımlar', isActive: true },
        { value: 'Diğer', isActive: true }
      ]);
    } catch (error) {
      console.error('Error fetching income sources:', error);
      toast.error('Gelir kaynakları yüklenirken bir hata oluştu.');
    } finally {
      setIsIncomeSourcesLoading(false);
    }
  };

  const fetchIncomeCategories = async () => {
    try {
      setIsIncomeCategoriesLoading(true);
      const incomeCategoriesRef = collection(db, 'DataEntryValues', 'income', 'IncomeCategories');
      const q = query(incomeCategoriesRef, orderBy('id', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedCategories = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          value: data.value,
          isActive: data.isActive !== false
        };
      });
      setIncomeCategories(fetchedCategories.length ? fetchedCategories : [
        { value: 'Ana Gelir', isActive: true },
        { value: 'Yan Gelir', isActive: true },
        { value: 'Faiz Geliri', isActive: true }
      ]);
    } catch (error) {
      console.error('Error fetching income categories:', error);
      toast.error('Gelir kategorileri yüklenirken bir hata oluştu.');
    } finally {
      setIsIncomeCategoriesLoading(false);
    }
  };

  const fetchExpenseSources = async () => {
    try {
      setIsExpenseSourcesLoading(true);
      const expenseSourcesRef = collection(db, 'DataEntryValues', 'expense', 'ExpenseSources');
      const q = query(expenseSourcesRef, orderBy('id', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedSources = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          value: data.value,
          isActive: data.isActive !== false
        };
      });
      setExpenseSources(fetchedSources.length ? fetchedSources : [
        { value: 'Satıcı', isActive: true },
        { value: 'Hizmet', isActive: true },
        { value: 'Diğer', isActive: true }
      ]);
    } catch (error) {
      console.error('Error fetching expense sources:', error);
      toast.error('Gider kaynakları yüklenirken bir hata oluştu.');
    } finally {
      setIsExpenseSourcesLoading(false);
    }
  };

  const fetchExpenseTypes = async () => {
    try {
      setIsExpenseTypesLoading(true);
      const expenseTypesRef = collection(db, 'DataEntryValues', 'expense', 'ExpenseTypes');
      const q = query(expenseTypesRef, orderBy('id', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedTypes = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          value: data.value,
          isActive: data.isActive !== false
        };
      });
      setExpenseTypes(fetchedTypes.length ? fetchedTypes : [
        { value: 'Gider', isActive: true },
        { value: 'Yatırım', isActive: true }
      ]);
    } catch (error) {
      console.error('Error fetching expense types:', error);
      toast.error('Gider tipleri yüklenirken bir hata oluştu.');
    } finally {
      setIsExpenseTypesLoading(false);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      setIsExpenseCategoriesLoading(true);
      const expenseCategoriesRef = collection(db, 'DataEntryValues', 'expense', 'ExpenseCategories');
      const q = query(expenseCategoriesRef, orderBy('id', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedCategories = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          value: data.value,
          isActive: data.isActive !== false
        };
      });
      setExpenseCategories(fetchedCategories.length ? fetchedCategories : [
        { value: 'Sabit Gider', isActive: true },
        { value: 'Değişken Gider', isActive: true },
        { value: 'Yatırım Gideri', isActive: true }
      ]);
    } catch (error) {
      console.error('Error fetching expense categories:', error);
      toast.error('Gider kategorileri yüklenirken bir hata oluştu.');
    } finally {
      setIsExpenseCategoriesLoading(false);
    }
  };

  const handleDepartmentChange = (index, value) => {
    const newDepartments = [...departments];
    newDepartments[index] = { ...newDepartments[index], value };
    setDepartments(newDepartments);
    
    // Clear error for this field if it exists
    if (errors.departments[index]) {
      const newErrors = {...errors};
      delete newErrors.departments[index];
      setErrors(newErrors);
    }
  };

  const handleAuthRoleChange = (index, value) => {
    const newRoles = [...authRoles];
    newRoles[index] = { ...newRoles[index], value };
    setAuthRoles(newRoles);
    
    // Clear error for this field if it exists
    if (errors.roles[index]) {
      const newErrors = {...errors};
      delete newErrors.roles[index];
      setErrors(newErrors);
    }
  };

  const handleIncomeSourceChange = (index, value) => {
    const newSources = [...incomeSources];
    newSources[index] = { ...newSources[index], value };
    setIncomeSources(newSources);
    
    if (errors.incomeSources[index]) {
      const newErrors = {...errors};
      delete newErrors.incomeSources[index];
      setErrors(newErrors);
    }
  };

  const handleIncomeCategoryChange = (index, value) => {
    const newCategories = [...incomeCategories];
    newCategories[index] = { ...newCategories[index], value };
    setIncomeCategories(newCategories);
    
    if (errors.incomeCategories[index]) {
      const newErrors = {...errors};
      delete newErrors.incomeCategories[index];
      setErrors(newErrors);
    }
  };

  const addNewDepartment = () => {
    setDepartments([...departments, { value: '', isActive: true }]);
    // Set new item to editing mode automatically
    const newEditingItems = {...editingItems};
    newEditingItems.departments[departments.length] = true;
    setEditingItems(newEditingItems);
  };

  const addNewAuthRole = () => {
    setAuthRoles([...authRoles, { value: '', isActive: true }]);
    // Set new item to editing mode automatically
    const newEditingItems = {...editingItems};
    newEditingItems.roles[authRoles.length] = true;
    setEditingItems(newEditingItems);
  };

  const addNewIncomeSource = () => {
    setIncomeSources([...incomeSources, { value: '', isActive: true }]);
    const newEditingItems = {...editingItems};
    newEditingItems.incomeSources[incomeSources.length] = true;
    setEditingItems(newEditingItems);
  };

  const addNewIncomeCategory = () => {
    setIncomeCategories([...incomeCategories, { value: '', isActive: true }]);
    const newEditingItems = {...editingItems};
    newEditingItems.incomeCategories[incomeCategories.length] = true;
    setEditingItems(newEditingItems);
  };

  const toggleEditDepartment = (index) => {
    const newEditingItems = {...editingItems};
    newEditingItems.departments[index] = !newEditingItems.departments[index];
    setEditingItems(newEditingItems);
  };

  const toggleEditRole = (index) => {
    const newEditingItems = {...editingItems};
    newEditingItems.roles[index] = !newEditingItems.roles[index];
    setEditingItems(newEditingItems);
  };

  const toggleEditIncomeSource = (index) => {
    const newEditingItems = {...editingItems};
    newEditingItems.incomeSources[index] = !newEditingItems.incomeSources[index];
    setEditingItems(newEditingItems);
  };

  const toggleEditIncomeCategory = (index) => {
    const newEditingItems = {...editingItems};
    newEditingItems.incomeCategories[index] = !newEditingItems.incomeCategories[index];
    setEditingItems(newEditingItems);
  };

  const checkDepartmentReferences = async (departmentValue) => {
    try {
      setIsCheckingReferences(true);
      console.log(`Checking department references for: "${departmentValue}"`);
      const employeesRef = collection(db, 'Employees');
      const q = query(employeesRef, where('department', '==', departmentValue));
      const querySnapshot = await getDocs(q);
      
      const inUse = !querySnapshot.empty;
      console.log(`Department "${departmentValue}" in use: ${inUse}`);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking department references:', error);
      toast.error('Departman referansları kontrol edilirken bir hata oluştu.');
      return false; // Assume it's in use to be safe
    } finally {
      setIsCheckingReferences(false);
    }
  };

  const checkRoleReferences = async (roleValue) => {
    try {
      setIsCheckingReferences(true);
      console.log(`Checking role references for: "${roleValue}"`);
      const usersRef = collection(db, 'Users');
      const q = query(usersRef, where('role', '==', roleValue));
      const querySnapshot = await getDocs(q);
      
      const inUse = !querySnapshot.empty;
      console.log(`Role "${roleValue}" in use: ${inUse}`);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking role references:', error);
      toast.error('Rol referansları kontrol edilirken bir hata oluştu.');
      return false; // Assume it's in use to be safe
    } finally {
      setIsCheckingReferences(false);
    }
  };

  const checkIncomeSourceReferences = async (sourceValue) => {
    try {
      setIsCheckingReferences(true);
      console.log(`Checking income source references for: "${sourceValue}"`);
      const budgetsRef = collection(db, 'Budgets');
      const q = query(budgetsRef, where('BütçeKaynak', '==', sourceValue));
      const querySnapshot = await getDocs(q);
      
      const inUse = !querySnapshot.empty;
      console.log(`Income source "${sourceValue}" in use: ${inUse}`);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking income source references:', error);
      toast.error('Gelir kaynağı referansları kontrol edilirken bir hata oluştu.');
      return false; // Assume it's in use to be safe
    } finally {
      setIsCheckingReferences(false);
    }
  };

  const checkIncomeCategoryReferences = async (categoryValue) => {
    try {
      setIsCheckingReferences(true);
      console.log(`Checking income category references for: "${categoryValue}"`);
      const budgetsRef = collection(db, 'Budgets');
      const q = query(budgetsRef, where('BütçeKategorisi', '==', categoryValue));
      const querySnapshot = await getDocs(q);
      
      const inUse = !querySnapshot.empty;
      console.log(`Income category "${categoryValue}" in use: ${inUse}`);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking income category references:', error);
      toast.error('Gelir kategorisi referansları kontrol edilirken bir hata oluştu.');
      return false; // Assume it's in use to be safe
    } finally {
      setIsCheckingReferences(false);
    }
  };

  const removeDepartment = async (index) => {
    const departmentToRemove = departments[index];
    console.log(`Attempting to remove department at index ${index}:`, departmentToRemove);
    
    // If it's a new, empty department, just remove it
    if (departmentToRemove.value.trim() === '') {
      const newDepartments = [...departments];
      newDepartments.splice(index, 1);
      // Ensure at least one department exists
      if (newDepartments.length === 0) {
        newDepartments.push({ value: '', isActive: true });
      }
      setDepartments(newDepartments);
      return;
    }
    
    console.log('Checking if department is in use...');
    const isInUse = await checkDepartmentReferences(departmentToRemove.value);
    console.log(`Is department "${departmentToRemove.value}" in use? ${isInUse}`);
    
    if (isInUse) {
      console.log('Department is in use, prompting for deactivation.');
      const confirmDeactivate = window.confirm(
        `Bu departman (${departmentToRemove.value}) çalışanlara atanmış durumda. Silmek yerine pasif duruma getirmek ister misiniz? ` +
        `Pasif departmanlar listede görünmeye devam edecek ancak yeni çalışanlar için seçilemeyecektir.`
      );
      
      if (confirmDeactivate) {
        const newDepartments = [...departments];
        newDepartments[index] = { ...newDepartments[index], isActive: false };
        setDepartments(newDepartments);
        toast.info('Departman pasif duruma getirildi.');
      }
    } else {
      console.log('Department not in use, prompting for deletion.');
      const confirmDelete = window.confirm('Bu departmanı silmek istediğinizden emin misiniz?');
      console.log(`Deletion confirmed: ${confirmDelete}`);
      
      if (confirmDelete) {
        const newDepartments = [...departments];
        console.log('Departments before splice:', JSON.stringify(newDepartments));
        newDepartments.splice(index, 1);
        // Ensure at least one department exists
        if (newDepartments.length === 0) {
          newDepartments.push({ value: '', isActive: true });
        }
        setDepartments(newDepartments);
        console.log('Departments after splice, before save:', JSON.stringify(newDepartments));
        toast.success('Departman başarıyla silindi.');
        // Trigger save immediately after deletion, passing the updated array
        console.log('Calling handleSave to persist deletion...');
        await handleSave(newDepartments);
        console.log('handleSave completed after deletion.');
      }
    }
  };

  const removeAuthRole = async (index) => {
    const roleToRemove = authRoles[index];
    console.log(`Attempting to remove role at index ${index}:`, roleToRemove);
    
    // If it's a new, empty role, just remove it
    if (roleToRemove.value.trim() === '') {
      const newRoles = [...authRoles];
      newRoles.splice(index, 1);
      // Ensure at least one role exists
      if (newRoles.length === 0) {
        newRoles.push({ value: '', isActive: true });
      }
      setAuthRoles(newRoles);
      return;
    }
    
    console.log('Checking if role is in use...');
    const isInUse = await checkRoleReferences(roleToRemove.value);
    console.log(`Is role "${roleToRemove.value}" in use? ${isInUse}`);
    
    if (isInUse) {
      console.log('Role is in use, prompting for deactivation.');
      const confirmDeactivate = window.confirm(
        `Bu rol (${roleToRemove.value}) kullanıcılara atanmış durumda. Silmek yerine pasif duruma getirmek ister misiniz? ` +
        `Pasif roller listede görünmeye devam edecek ancak yeni kullanıcılar için seçilemeyecektir.`
      );
      
      if (confirmDeactivate) {
        const newRoles = [...authRoles];
        newRoles[index] = { ...newRoles[index], isActive: false };
        setAuthRoles(newRoles);
        toast.info('Rol pasif duruma getirildi.');
      }
    } else {
      console.log('Role not in use, prompting for deletion.');
      const confirmDelete = window.confirm('Bu rolü silmek istediğinizden emin misiniz?');
      console.log(`Deletion confirmed: ${confirmDelete}`);
      
      if (confirmDelete) {
        const newRoles = [...authRoles];
        console.log('Roles before splice:', JSON.stringify(newRoles));
        newRoles.splice(index, 1);
        // Ensure at least one role exists
        if (newRoles.length === 0) {
          newRoles.push({ value: '', isActive: true });
        }
        setAuthRoles(newRoles);
        console.log('Roles after splice, before save:', JSON.stringify(newRoles));
        toast.success('Rol başarıyla silindi.');
        // Trigger save immediately after deletion, passing the updated array
        console.log('Calling handleSaveAuthRoles to persist deletion...');
        await handleSaveAuthRoles(newRoles);
        console.log('handleSaveAuthRoles completed after deletion.');
      }
    }
  };

  const removeIncomeSource = async (index) => {
    const sourceToRemove = incomeSources[index];
    console.log(`Attempting to remove income source at index ${index}:`, sourceToRemove);
    
    // If it's a new, empty source, just remove it
    if (sourceToRemove.value.trim() === '') {
      const newSources = [...incomeSources];
      newSources.splice(index, 1);
      // Ensure at least one source exists
      if (newSources.length === 0) {
        newSources.push({ value: '', isActive: true });
      }
      setIncomeSources(newSources);
      return;
    }
    
    console.log('Checking if source is in use...');
    const isInUse = await checkIncomeSourceReferences(sourceToRemove.value);
    console.log(`Is source "${sourceToRemove.value}" in use? ${isInUse}`);
    
    if (isInUse) {
      console.log('Source is in use, prompting for deactivation.');
      const confirmDeactivate = window.confirm(
        `Bu gelir kaynağı (${sourceToRemove.value}) çalışanlara atanmış durumda. Silmek yerine pasif duruma getirmek ister misiniz? ` +
        `Pasif gelir kaynakları listede görünmeye devam edecek ancak yeni çalışanlar için seçilemeyecektir.`
      );
      
      if (confirmDeactivate) {
        const newSources = [...incomeSources];
        newSources[index] = { ...newSources[index], isActive: false };
        setIncomeSources(newSources);
        toast.info('Gelir kaynağı pasif duruma getirildi.');
      }
    } else {
      console.log('Source not in use, prompting for deletion.');
      const confirmDelete = window.confirm('Bu gelir kaynağını silmek istediğinizden emin misiniz?');
      console.log(`Deletion confirmed: ${confirmDelete}`);
      
      if (confirmDelete) {
        const newSources = [...incomeSources];
        console.log('Sources before splice:', JSON.stringify(newSources));
        newSources.splice(index, 1);
        // Ensure at least one source exists
        if (newSources.length === 0) {
          newSources.push({ value: '', isActive: true });
        }
        setIncomeSources(newSources);
        console.log('Sources after splice, before save:', JSON.stringify(newSources));
        toast.success('Gelir kaynağı başarıyla silindi.');
        // Trigger save immediately after deletion, passing the updated array
        console.log('Calling handleSaveIncomeSources to persist deletion...');
        await handleSaveIncomeSources(newSources);
        console.log('handleSaveIncomeSources completed after deletion.');
      }
    }
  };

  const removeIncomeCategory = async (index) => {
    const categoryToRemove = incomeCategories[index];
    console.log(`Attempting to remove income category at index ${index}:`, categoryToRemove);
    
    // If it's a new, empty category, just remove it
    if (categoryToRemove.value.trim() === '') {
      const newCategories = [...incomeCategories];
      newCategories.splice(index, 1);
      // Ensure at least one category exists
      if (newCategories.length === 0) {
        newCategories.push({ value: '', isActive: true });
      }
      setIncomeCategories(newCategories);
      return;
    }
    
    console.log('Checking if category is in use...');
    const isInUse = await checkIncomeCategoryReferences(categoryToRemove.value);
    console.log(`Is category "${categoryToRemove.value}" in use? ${isInUse}`);
    
    if (isInUse) {
      console.log('Category is in use, prompting for deactivation.');
      const confirmDeactivate = window.confirm(
        `Bu gelir kategorisi (${categoryToRemove.value}) çalışanlara atanmış durumda. Silmek yerine pasif duruma getirmek ister misiniz? ` +
        `Pasif gelir kategorileri listede görünmeye devam edecek ancak yeni çalışanlar için seçilemeyecektir.`
      );
      
      if (confirmDeactivate) {
        const newCategories = [...incomeCategories];
        newCategories[index] = { ...newCategories[index], isActive: false };
        setIncomeCategories(newCategories);
        toast.info('Gelir kategorisi pasif duruma getirildi.');
      }
    } else {
      console.log('Category not in use, prompting for deletion.');
      const confirmDelete = window.confirm('Bu gelir kategorisini silmek istediğinizden emin misiniz?');
      console.log(`Deletion confirmed: ${confirmDelete}`);
      
      if (confirmDelete) {
        const newCategories = [...incomeCategories];
        console.log('Categories before splice:', JSON.stringify(newCategories));
        newCategories.splice(index, 1);
        // Ensure at least one category exists
        if (newCategories.length === 0) {
          newCategories.push({ value: '', isActive: true });
        }
        setIncomeCategories(newCategories);
        console.log('Categories after splice, before save:', JSON.stringify(newCategories));
        toast.success('Gelir kategorisi başarıyla silindi.');
        // Trigger save immediately after deletion, passing the updated array
        console.log('Calling handleSaveIncomeCategories to persist deletion...');
        await handleSaveIncomeCategories(newCategories);
        console.log('handleSaveIncomeCategories completed after deletion.');
      }
    }
  };

  const validateDepartments = () => {
    const newErrors = { ...errors };
    newErrors.departments = {};
    let isValid = true;
    
    // Check for duplicates and empty required fields
    const validDepartments = departments.filter(dept => dept.value.trim() !== '');
    const nameCounts = {};
    
    validDepartments.forEach((dept, index) => {
      const trimmedDept = dept.value.trim();
      nameCounts[trimmedDept] = (nameCounts[trimmedDept] || 0) + 1;
      
      if (nameCounts[trimmedDept] > 1) {
        newErrors.departments[index] = 'Bu departman zaten listede mevcut.';
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    return isValid;
  };

  const validateRoles = () => {
    const newErrors = { ...errors };
    newErrors.roles = {};
    let isValid = true;
    
    // Check for duplicates and empty required fields
    const validRoles = authRoles.filter(role => role.value.trim() !== '');
    const nameCounts = {};
    
    validRoles.forEach((role, index) => {
      const trimmedRole = role.value.trim();
      nameCounts[trimmedRole] = (nameCounts[trimmedRole] || 0) + 1;
      
      if (nameCounts[trimmedRole] > 1) {
        newErrors.roles[index] = 'Bu rol zaten listede mevcut.';
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    return isValid;
  };

  const validateIncomeSources = () => {
    const newErrors = { ...errors };
    newErrors.incomeSources = {};
    let isValid = true;
    
    const validSources = incomeSources.filter(source => source.value.trim() !== '');
    const nameCounts = {};
    
    validSources.forEach((source, index) => {
      const trimmedSource = source.value.trim();
      nameCounts[trimmedSource] = (nameCounts[trimmedSource] || 0) + 1;
      
      if (nameCounts[trimmedSource] > 1) {
        newErrors.incomeSources[index] = 'Bu gelir kaynağı zaten listede mevcut.';
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    return isValid;
  };

  const validateIncomeCategories = () => {
    const newErrors = { ...errors };
    newErrors.incomeCategories = {};
    let isValid = true;
    
    const validCategories = incomeCategories.filter(category => category.value.trim() !== '');
    const nameCounts = {};
    
    validCategories.forEach((category, index) => {
      const trimmedCategory = category.value.trim();
      nameCounts[trimmedCategory] = (nameCounts[trimmedCategory] || 0) + 1;
      
      if (nameCounts[trimmedCategory] > 1) {
        newErrors.incomeCategories[index] = 'Bu gelir kategorisi zaten listede mevcut.';
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async (departmentsToSave = departments) => {
    if (!validateDepartments()) {
      toast.error('Lütfen girdiğiniz departman bilgilerini kontrol edin.');
      return;
    }

    console.log('handleSave started.');

    try {
      setIsSaving(true);
      const batch = writeBatch(db);
      
      // Reference to the EmployeeDepartments subcollection
      const employeeDepartmentsRef = collection(db, 'DataEntryValues', 'departments', 'EmployeeDepartments');

      // --- Delete existing documents first ---
      console.log('Fetching existing department documents to delete...');
      const existingDocsSnapshot = await getDocs(employeeDepartmentsRef);
      existingDocsSnapshot.forEach(doc => {
        console.log(`Adding delete operation for existing department doc: ${doc.id}`);
        batch.delete(doc.ref);
      });
      // --------------------------------------

      // Filter out empty departments from the desired state
      const validDepartments = departmentsToSave.filter(dept => dept.value.trim() !== '');

      console.log('Saving these departments:', JSON.stringify(validDepartments));

      // Create batch operations for each department
      validDepartments.forEach((department, index) => {
        const deptDoc = doc(employeeDepartmentsRef, index.toString());
        batch.set(deptDoc, {
          id: index,
          value: department.value.trim(),
          isActive: department.isActive
        });
      });

      // Commit the batch
      await batch.commit();
      
      console.log('Department batch commit successful.');
      toast.success('Departmanlar başarıyla kaydedildi!');
      // Refresh the departments after saving
      await fetchDepartments();
      
      // Turn off all edit modes
      const newEditingItems = {...editingItems};
      newEditingItems.departments = {};
      setEditingItems(newEditingItems);
    } catch (error) {
      console.error('Error saving departments:', error);
      console.log('Error details during department save:', error);
      toast.error('Departmanları kaydederken bir hata oluştu: ' + error.message);
    } finally {
      setIsSaving(false);
      console.log('handleSave finished.');
    }
  };

  const handleSaveAuthRoles = async (rolesToSave = authRoles) => {
    if (!validateRoles()) {
      toast.error('Lütfen girdiğiniz rol bilgilerini kontrol edin.');
      return;
    }

    console.log('handleSaveAuthRoles started.');

    try {
      setIsRolesSaving(true);
      const batch = writeBatch(db);
      
      // Reference to the AuthorizationRoles subcollection
      const authRolesRef = collection(db, 'DataEntryValues', 'roles', 'AuthorizationRoles');

      // --- Delete existing documents first ---
      console.log('Fetching existing role documents to delete...');
      const existingDocsSnapshot = await getDocs(authRolesRef);
      existingDocsSnapshot.forEach(doc => {
        console.log(`Adding delete operation for existing role doc: ${doc.id}`);
        batch.delete(doc.ref);
      });
      // --------------------------------------

      // Filter out empty roles from the desired state
      const validRoles = rolesToSave.filter(role => role.value.trim() !== '');

      console.log('Saving these roles:', JSON.stringify(validRoles));

      // Create batch operations for each role
      validRoles.forEach((role, index) => {
        const roleDoc = doc(authRolesRef, index.toString());
        batch.set(roleDoc, {
          id: index,
          value: role.value.trim(),
          isActive: role.isActive
        });
      });

      // Commit the batch
      await batch.commit();
      
      console.log('Role batch commit successful.');
      toast.success('Yetkilendirme rolleri başarıyla kaydedildi!');
      // Refresh the roles after saving
      await fetchAuthRoles();
      
      // Turn off all edit modes
      const newEditingItems = {...editingItems};
      newEditingItems.roles = {};
      setEditingItems(newEditingItems);
    } catch (error) {
      console.error('Error saving authorization roles:', error);
      console.log('Error details during role save:', error);
      toast.error('Yetkilendirme rollerini kaydederken bir hata oluştu: ' + error.message);
    } finally {
      setIsRolesSaving(false);
      console.log('handleSaveAuthRoles finished.');
    }
  };

  const handleSaveIncomeSources = async (sourcesToSave = incomeSources) => {
    if (!validateIncomeSources()) {
      toast.error('Lütfen girdiğiniz gelir kaynaklarını kontrol edin.');
      return;
    }

    try {
      setIsIncomeSourcesSaving(true);
      const batch = writeBatch(db);
      
      const incomeSourcesRef = collection(db, 'DataEntryValues', 'income', 'IncomeSources');

      const existingDocsSnapshot = await getDocs(incomeSourcesRef);
      existingDocsSnapshot.forEach(doc => {
        batch.delete(doc.ref);
      });

      const validSources = sourcesToSave.filter(source => source.value.trim() !== '');

      validSources.forEach((source, index) => {
        const sourceDoc = doc(incomeSourcesRef, index.toString());
        batch.set(sourceDoc, {
          id: index,
          value: source.value.trim(),
          isActive: source.isActive
        });
      });

      await batch.commit();
      
      toast.success('Gelir kaynakları başarıyla kaydedildi!');
      await fetchIncomeSources();
      
      const newEditingItems = {...editingItems};
      newEditingItems.incomeSources = {};
      setEditingItems(newEditingItems);
    } catch (error) {
      console.error('Error saving income sources:', error);
      toast.error('Gelir kaynakları kaydedilirken bir hata oluştu: ' + error.message);
    } finally {
      setIsIncomeSourcesSaving(false);
    }
  };

  const handleSaveIncomeCategories = async (categoriesToSave = incomeCategories) => {
    if (!validateIncomeCategories()) {
      toast.error('Lütfen girdiğiniz gelir kategorilerini kontrol edin.');
      return;
    }

    try {
      setIsIncomeCategoriesSaving(true);
      const batch = writeBatch(db);
      
      const incomeCategoriesRef = collection(db, 'DataEntryValues', 'income', 'IncomeCategories');

      const existingDocsSnapshot = await getDocs(incomeCategoriesRef);
      existingDocsSnapshot.forEach(doc => {
        batch.delete(doc.ref);
      });

      const validCategories = categoriesToSave.filter(category => category.value.trim() !== '');

      validCategories.forEach((category, index) => {
        const categoryDoc = doc(incomeCategoriesRef, index.toString());
        batch.set(categoryDoc, {
          id: index,
          value: category.value.trim(),
          isActive: category.isActive
        });
      });

      await batch.commit();
      
      toast.success('Gelir kategorileri başarıyla kaydedildi!');
      await fetchIncomeCategories();
      
      const newEditingItems = {...editingItems};
      newEditingItems.incomeCategories = {};
      setEditingItems(newEditingItems);
    } catch (error) {
      console.error('Error saving income categories:', error);
      toast.error('Gelir kategorileri kaydedilirken bir hata oluştu: ' + error.message);
    } finally {
      setIsIncomeCategoriesSaving(false);
    }
  };

  const handleKeyDown = (e, type, index, isLast) => {
    // If Enter key is pressed
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isLast) {
        // Add a new field if this is the last one
        type === 'department' ? addNewDepartment() : type === 'role' ? addNewAuthRole() : type === 'income-source' ? addNewIncomeSource() : addNewIncomeCategory();
      }
    }
  };

  const saveExpenseSources = async () => {
    try {
      setIsExpenseSourcesSaving(true);
      const batch = writeBatch(db);
      const expenseSourcesRef = collection(db, 'DataEntryValues', 'expense', 'ExpenseSources');

      // Delete existing documents
      const existingDocs = await getDocs(expenseSourcesRef);
      existingDocs.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Add new documents
      expenseSources.forEach((source, index) => {
        const docRef = doc(expenseSourcesRef);
        batch.set(docRef, {
          id: index + 1,
          value: source.value,
          isActive: source.isActive
        });
      });

      await batch.commit();
      toast.success('Gider kaynakları başarıyla kaydedildi.');
    } catch (error) {
      console.error('Error saving expense sources:', error);
      toast.error('Gider kaynakları kaydedilirken bir hata oluştu.');
    } finally {
      setIsExpenseSourcesSaving(false);
    }
  };

  const saveExpenseTypes = async () => {
    try {
      setIsExpenseTypesSaving(true);
      const batch = writeBatch(db);
      const expenseTypesRef = collection(db, 'DataEntryValues', 'expense', 'ExpenseTypes');

      // Delete existing documents
      const existingDocs = await getDocs(expenseTypesRef);
      existingDocs.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Add new documents
      expenseTypes.forEach((type, index) => {
        const docRef = doc(expenseTypesRef);
        batch.set(docRef, {
          id: index + 1,
          value: type.value,
          isActive: type.isActive
        });
      });

      await batch.commit();
      toast.success('Gider tipleri başarıyla kaydedildi.');
    } catch (error) {
      console.error('Error saving expense types:', error);
      toast.error('Gider tipleri kaydedilirken bir hata oluştu.');
    } finally {
      setIsExpenseTypesSaving(false);
    }
  };

  const saveExpenseCategories = async () => {
    try {
      setIsExpenseCategoriesSaving(true);
      const batch = writeBatch(db);
      const expenseCategoriesRef = collection(db, 'DataEntryValues', 'expense', 'ExpenseCategories');

      // Delete existing documents
      const existingDocs = await getDocs(expenseCategoriesRef);
      existingDocs.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Add new documents
      expenseCategories.forEach((category, index) => {
        const docRef = doc(expenseCategoriesRef);
        batch.set(docRef, {
          id: index + 1,
          value: category.value,
          isActive: category.isActive
        });
      });

      await batch.commit();
      toast.success('Gider kategorileri başarıyla kaydedildi.');
    } catch (error) {
      console.error('Error saving expense categories:', error);
      toast.error('Gider kategorileri kaydedilirken bir hata oluştu.');
    } finally {
      setIsExpenseCategoriesSaving(false);
    }
  };

  if (isLoading && isRolesLoading && isIncomeSourcesLoading && isIncomeCategoriesLoading && isExpenseSourcesLoading && isExpenseTypesLoading && isExpenseCategoriesLoading) {
    return (
      <div className={styles.container} aria-busy="true">
        <div className={styles.header}>
          <h1 className={styles.title}>
            <FaList className={styles.titleIcon} aria-hidden="true" />
            <span>Veri Giriş Değerleri</span>
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <Oval
            color="#4f46e5"
            secondaryColor="#818cf8"
            height={50}
            width={50}
            visible={true}
          />
          <p>Yükleniyor, lütfen bekleyin...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <FaList className={styles.titleIcon} aria-hidden="true" />
          <span>Veri Giriş Değerleri</span>
        </h1>
      </div>

      <div className={styles.formContainer} aria-labelledby="departments-heading">
        <h2 id="departments-heading" className={styles.sectionHeading}>Çalışan Menü - Departmanlar</h2>
        <p className={styles.helperText}>Çalışanlar için mevcut departmanları düzenleyin. Boş departmanlar kaydedilmeyecektir.</p>
        
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Oval color="#4f46e5" secondaryColor="#818cf8" height={30} width={30} visible={true} />
            <p>Departmanlar yükleniyor...</p>
          </div>
        ) : (
          <div className={styles.departmentsGrid}>
            {departments.map((department, index) => (
              <div key={index} className={styles.formField}>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    id={`department-${index}`}
                    value={department.value}
                    onChange={(e) => handleDepartmentChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'department', index, index === departments.length - 1)}
                    className={`${styles.input} ${errors.departments[index] ? styles.inputError : ''} ${!department.isActive ? styles.inactiveItem : ''}`}
                    placeholder="Departman adı giriniz"
                    aria-label={`Departman ${index + 1}${!department.isActive ? ' (Pasif)' : ''}`}
                    aria-invalid={errors.departments[index] ? "true" : "false"}
                    readOnly={!editingItems.departments[index]}
                    style={{cursor: editingItems.departments[index] ? 'text' : 'default'}}
                  />
                  {!department.isActive && (
                    <span className={styles.inactiveLabel}>(Pasif)</span>
                  )}
                  <div className={styles.itemActions}>
                    <button 
                      type="button"
                      onClick={() => toggleEditDepartment(index)}
                      className={styles.editButton}
                      aria-label={editingItems.departments[index] ? 'Düzenlemeyi bitir' : 'Departmanı düzenle'}
                      title={editingItems.departments[index] ? 'Düzenlemeyi bitir' : 'Düzenle'}
                    >
                      {editingItems.departments[index] ? (
                        <FaCheck size={14} aria-hidden="true" />
                      ) : (
                        <FaEdit size={14} aria-hidden="true" />
                      )}
                    </button>
                    <button 
                      type="button"
                      onClick={() => removeDepartment(index)}
                      className={styles.trashButton}
                      aria-label={`Departman ${index + 1} sil`}
                      title="Sil"
                      disabled={isSaving || isCheckingReferences}
                    >
                      <FaTrash size={14} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {errors.departments[index] && (
                  <p className={styles.errorText} id={`dept-error-${index}`}>{errors.departments[index]}</p>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className={styles.buttonContainer}>
          <button 
            type="button"
            onClick={() => handleSave()}
            className={styles.saveButton}
            disabled={isSaving || isLoading}
            aria-busy={isSaving}
          >
            <FaSave size={14} aria-hidden="true" />
            <span>{isSaving ? 'Kaydediliyor...' : 'Kaydet'}</span>
          </button>
          <button 
            type="button"
            onClick={addNewDepartment}
            className={styles.addButton}
            disabled={isSaving || isLoading}
          >
            <FaPlus size={14} aria-hidden="true" />
            <span>Yeni Ekle</span>
          </button>
        </div>
      </div>

      <div className={styles.formContainer} aria-labelledby="roles-heading">
        <h2 id="roles-heading" className={styles.sectionHeading}>Yetkilendirme Rolleri</h2>
        <p className={styles.helperText}>Sistem kullanıcıları için yetkilendirme rollerini düzenleyin. Boş roller kaydedilmeyecektir.</p>
        
        {isRolesLoading ? (
          <div className={styles.loadingContainer}>
            <Oval color="#4f46e5" secondaryColor="#818cf8" height={30} width={30} visible={true} />
            <p>Roller yükleniyor...</p>
          </div>
        ) : (
          <div className={styles.departmentsGrid}>
            {authRoles.map((role, index) => (
              <div key={index} className={styles.formField}>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    id={`role-${index}`}
                    value={role.value}
                    onChange={(e) => handleAuthRoleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'role', index, index === authRoles.length - 1)}
                    className={`${styles.input} ${errors.roles[index] ? styles.inputError : ''} ${!role.isActive ? styles.inactiveItem : ''}`}
                    placeholder="Rol adı giriniz"
                    aria-label={`Rol ${index + 1}${!role.isActive ? ' (Pasif)' : ''}`}
                    aria-invalid={errors.roles[index] ? "true" : "false"}
                    readOnly={!editingItems.roles[index]}
                    style={{cursor: editingItems.roles[index] ? 'text' : 'default'}}
                  />
                  {!role.isActive && (
                    <span className={styles.inactiveLabel}>(Pasif)</span>
                  )}
                  <div className={styles.itemActions}>
                    <button 
                      type="button"
                      onClick={() => toggleEditRole(index)}
                      className={styles.editButton}
                      aria-label={editingItems.roles[index] ? 'Düzenlemeyi bitir' : 'Rolü düzenle'}
                      title={editingItems.roles[index] ? 'Düzenlemeyi bitir' : 'Düzenle'}
                    >
                      {editingItems.roles[index] ? (
                        <FaCheck size={14} aria-hidden="true" />
                      ) : (
                        <FaEdit size={14} aria-hidden="true" />
                      )}
                    </button>
                    <button 
                      type="button"
                      onClick={() => removeAuthRole(index)}
                      className={styles.trashButton}
                      aria-label={`Rol ${index + 1} sil`}
                      title="Sil"
                      disabled={isRolesSaving || isCheckingReferences}
                    >
                      <FaTrash size={14} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {errors.roles[index] && (
                  <p className={styles.errorText} id={`role-error-${index}`}>{errors.roles[index]}</p>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className={styles.buttonContainer}>
          <button 
            type="button"
            onClick={() => handleSaveAuthRoles()}
            className={styles.saveButton}
            disabled={isRolesSaving || isRolesLoading}
            aria-busy={isRolesSaving}
          >
            <FaSave size={14} aria-hidden="true" />
            <span>{isRolesSaving ? 'Kaydediliyor...' : 'Kaydet'}</span>
          </button>
          <button 
            type="button"
            onClick={addNewAuthRole}
            className={styles.addButton}
            disabled={isRolesSaving || isRolesLoading}
          >
            <FaPlus size={14} aria-hidden="true" />
            <span>Yeni Ekle</span>
          </button>
        </div>
      </div>

      <div className={styles.formContainer} aria-labelledby="income-sources-heading">
        <h2 id="income-sources-heading" className={styles.sectionHeading}>Gelir Kaynakları</h2>
        <p className={styles.helperText}>Gelir kaynaklarını düzenleyin. Boş kaynaklar kaydedilmeyecektir.</p>
        
        {isIncomeSourcesLoading ? (
          <div className={styles.loadingContainer}>
            <Oval color="#4f46e5" secondaryColor="#818cf8" height={30} width={30} visible={true} />
            <p>Gelir kaynakları yükleniyor...</p>
          </div>
        ) : (
          <div className={styles.departmentsGrid}>
            {incomeSources.map((source, index) => (
              <div key={index} className={styles.formField}>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    id={`income-source-${index}`}
                    value={source.value}
                    onChange={(e) => handleIncomeSourceChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'income-source', index, index === incomeSources.length - 1)}
                    className={`${styles.input} ${errors.incomeSources[index] ? styles.inputError : ''} ${!source.isActive ? styles.inactiveItem : ''}`}
                    placeholder="Gelir kaynağı giriniz"
                    aria-label={`Gelir kaynağı ${index + 1}${!source.isActive ? ' (Pasif)' : ''}`}
                    aria-invalid={errors.incomeSources[index] ? "true" : "false"}
                    readOnly={!editingItems.incomeSources[index]}
                    style={{cursor: editingItems.incomeSources[index] ? 'text' : 'default'}}
                  />
                  {!source.isActive && (
                    <span className={styles.inactiveLabel}>(Pasif)</span>
                  )}
                  <div className={styles.itemActions}>
                    <button 
                      type="button"
                      onClick={() => toggleEditIncomeSource(index)}
                      className={styles.editButton}
                      aria-label={editingItems.incomeSources[index] ? 'Düzenlemeyi bitir' : 'Gelir kaynağını düzenle'}
                      title={editingItems.incomeSources[index] ? 'Düzenlemeyi bitir' : 'Düzenle'}
                    >
                      {editingItems.incomeSources[index] ? (
                        <FaCheck size={14} aria-hidden="true" />
                      ) : (
                        <FaEdit size={14} aria-hidden="true" />
                      )}
                    </button>
                    <button 
                      type="button"
                      onClick={() => removeIncomeSource(index)}
                      className={styles.trashButton}
                      aria-label={`Gelir kaynağı ${index + 1} sil`}
                      title="Sil"
                      disabled={isIncomeSourcesSaving}
                    >
                      <FaTrash size={14} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {errors.incomeSources[index] && (
                  <p className={styles.errorText} id={`income-source-error-${index}`}>{errors.incomeSources[index]}</p>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className={styles.buttonContainer}>
          <button 
            type="button"
            onClick={() => handleSaveIncomeSources()}
            className={styles.saveButton}
            disabled={isIncomeSourcesSaving || isIncomeSourcesLoading}
            aria-busy={isIncomeSourcesSaving}
          >
            <FaSave size={14} aria-hidden="true" />
            <span>{isIncomeSourcesSaving ? 'Kaydediliyor...' : 'Kaydet'}</span>
          </button>
          <button 
            type="button"
            onClick={addNewIncomeSource}
            className={styles.addButton}
            disabled={isIncomeSourcesSaving || isIncomeSourcesLoading}
          >
            <FaPlus size={14} aria-hidden="true" />
            <span>Yeni Ekle</span>
          </button>
        </div>
      </div>

      <div className={styles.formContainer} aria-labelledby="income-categories-heading">
        <h2 id="income-categories-heading" className={styles.sectionHeading}>Gelir Kategorileri</h2>
        <p className={styles.helperText}>Gelir kategorilerini düzenleyin. Boş kategoriler kaydedilmeyecektir.</p>
        
        {isIncomeCategoriesLoading ? (
          <div className={styles.loadingContainer}>
            <Oval color="#4f46e5" secondaryColor="#818cf8" height={30} width={30} visible={true} />
            <p>Gelir kategorileri yükleniyor...</p>
          </div>
        ) : (
          <div className={styles.departmentsGrid}>
            {incomeCategories.map((category, index) => (
              <div key={index} className={styles.formField}>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    id={`income-category-${index}`}
                    value={category.value}
                    onChange={(e) => handleIncomeCategoryChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'income-category', index, index === incomeCategories.length - 1)}
                    className={`${styles.input} ${errors.incomeCategories[index] ? styles.inputError : ''} ${!category.isActive ? styles.inactiveItem : ''}`}
                    placeholder="Gelir kategorisi giriniz"
                    aria-label={`Gelir kategorisi ${index + 1}${!category.isActive ? ' (Pasif)' : ''}`}
                    aria-invalid={errors.incomeCategories[index] ? "true" : "false"}
                    readOnly={!editingItems.incomeCategories[index]}
                    style={{cursor: editingItems.incomeCategories[index] ? 'text' : 'default'}}
                  />
                  {!category.isActive && (
                    <span className={styles.inactiveLabel}>(Pasif)</span>
                  )}
                  <div className={styles.itemActions}>
                    <button 
                      type="button"
                      onClick={() => toggleEditIncomeCategory(index)}
                      className={styles.editButton}
                      aria-label={editingItems.incomeCategories[index] ? 'Düzenlemeyi bitir' : 'Gelir kategorisini düzenle'}
                      title={editingItems.incomeCategories[index] ? 'Düzenlemeyi bitir' : 'Düzenle'}
                    >
                      {editingItems.incomeCategories[index] ? (
                        <FaCheck size={14} aria-hidden="true" />
                      ) : (
                        <FaEdit size={14} aria-hidden="true" />
                      )}
                    </button>
                    <button 
                      type="button"
                      onClick={() => removeIncomeCategory(index)}
                      className={styles.trashButton}
                      aria-label={`Gelir kategorisi ${index + 1} sil`}
                      title="Sil"
                      disabled={isIncomeCategoriesSaving}
                    >
                      <FaTrash size={14} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {errors.incomeCategories[index] && (
                  <p className={styles.errorText} id={`income-category-error-${index}`}>{errors.incomeCategories[index]}</p>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className={styles.buttonContainer}>
          <button 
            type="button"
            onClick={() => handleSaveIncomeCategories()}
            className={styles.saveButton}
            disabled={isIncomeCategoriesSaving || isIncomeCategoriesLoading}
            aria-busy={isIncomeCategoriesSaving}
          >
            <FaSave size={14} aria-hidden="true" />
            <span>{isIncomeCategoriesSaving ? 'Kaydediliyor...' : 'Kaydet'}</span>
          </button>
          <button 
            type="button"
            onClick={addNewIncomeCategory}
            className={styles.addButton}
            disabled={isIncomeCategoriesSaving || isIncomeCategoriesLoading}
          >
            <FaPlus size={14} aria-hidden="true" />
            <span>Yeni Ekle</span>
          </button>
        </div>
      </div>

      <div className={styles.expenseSection}>
        <h2 className={styles.expenseSectionTitle}>
          <FaList className={styles.expenseSectionTitleIcon} />
          Gider Yönetimi
        </h2>
        <div className={styles.expenseGrid}>
          {/* Expense Sources */}
          <div className={styles.expenseCard}>
            <h3 className={styles.expenseCardTitle}>Gider Kaynakları</h3>
            <div className="space-y-4">
              {expenseSources.map((source, index) => (
                <div key={index} className={styles.expenseItem}>
                  <input
                    type="text"
                    value={source.value}
                    onChange={(e) => {
                      const newSources = [...expenseSources];
                      newSources[index].value = e.target.value;
                      setExpenseSources(newSources);
                    }}
                    className={styles.expenseInput}
                    placeholder="Gider kaynağı"
                  />
                  <button
                    onClick={() => {
                      const newSources = [...expenseSources];
                      newSources[index].isActive = !newSources[index].isActive;
                      setExpenseSources(newSources);
                    }}
                    className={`${styles.expenseButton} ${
                      source.isActive ? styles.expenseButtonActive : styles.expenseButtonInactive
                    }`}
                  >
                    {source.isActive ? 'Aktif' : 'Pasif'}
                  </button>
                  <button
                    onClick={() => {
                      const newSources = [...expenseSources];
                      newSources.splice(index, 1);
                      setExpenseSources(newSources);
                    }}
                    className={`${styles.expenseButton} ${styles.expenseButtonDelete}`}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setExpenseSources([...expenseSources, { value: '', isActive: true }])}
                className={`${styles.expenseButton} ${styles.expenseButtonAdd}`}
              >
                Yeni Gider Kaynağı Ekle
              </button>
              <button
                onClick={saveExpenseSources}
                disabled={isExpenseSourcesSaving}
                className={`${styles.expenseButton} ${styles.expenseButtonSave}`}
              >
                {isExpenseSourcesSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>

          {/* Expense Types */}
          <div className={styles.expenseCard}>
            <h3 className={styles.expenseCardTitle}>Gider Tipleri</h3>
            <div className="space-y-4">
              {expenseTypes.map((type, index) => (
                <div key={index} className={styles.expenseItem}>
                  <input
                    type="text"
                    value={type.value}
                    onChange={(e) => {
                      const newTypes = [...expenseTypes];
                      newTypes[index].value = e.target.value;
                      setExpenseTypes(newTypes);
                    }}
                    className={styles.expenseInput}
                    placeholder="Gider tipi"
                  />
                  <button
                    onClick={() => {
                      const newTypes = [...expenseTypes];
                      newTypes[index].isActive = !newTypes[index].isActive;
                      setExpenseTypes(newTypes);
                    }}
                    className={`${styles.expenseButton} ${
                      type.isActive ? styles.expenseButtonActive : styles.expenseButtonInactive
                    }`}
                  >
                    {type.isActive ? 'Aktif' : 'Pasif'}
                  </button>
                  <button
                    onClick={() => {
                      const newTypes = [...expenseTypes];
                      newTypes.splice(index, 1);
                      setExpenseTypes(newTypes);
                    }}
                    className={`${styles.expenseButton} ${styles.expenseButtonDelete}`}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setExpenseTypes([...expenseTypes, { value: '', isActive: true }])}
                className={`${styles.expenseButton} ${styles.expenseButtonAdd}`}
              >
                Yeni Gider Tipi Ekle
              </button>
              <button
                onClick={saveExpenseTypes}
                disabled={isExpenseTypesSaving}
                className={`${styles.expenseButton} ${styles.expenseButtonSave}`}
              >
                {isExpenseTypesSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>

          {/* Expense Categories */}
          <div className={styles.expenseCard}>
            <h3 className={styles.expenseCardTitle}>Gider Kategorileri</h3>
            <div className="space-y-4">
              {expenseCategories.map((category, index) => (
                <div key={index} className={styles.expenseItem}>
                  <input
                    type="text"
                    value={category.value}
                    onChange={(e) => {
                      const newCategories = [...expenseCategories];
                      newCategories[index].value = e.target.value;
                      setExpenseCategories(newCategories);
                    }}
                    className={styles.expenseInput}
                    placeholder="Gider kategorisi"
                  />
                  <button
                    onClick={() => {
                      const newCategories = [...expenseCategories];
                      newCategories[index].isActive = !newCategories[index].isActive;
                      setExpenseCategories(newCategories);
                    }}
                    className={`${styles.expenseButton} ${
                      category.isActive ? styles.expenseButtonActive : styles.expenseButtonInactive
                    }`}
                  >
                    {category.isActive ? 'Aktif' : 'Pasif'}
                  </button>
                  <button
                    onClick={() => {
                      const newCategories = [...expenseCategories];
                      newCategories.splice(index, 1);
                      setExpenseCategories(newCategories);
                    }}
                    className={`${styles.expenseButton} ${styles.expenseButtonDelete}`}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setExpenseCategories([...expenseCategories, { value: '', isActive: true }])}
                className={`${styles.expenseButton} ${styles.expenseButtonAdd}`}
              >
                Yeni Gider Kategorisi Ekle
              </button>
              <button
                onClick={saveExpenseCategories}
                disabled={isExpenseCategoriesSaving}
                className={`${styles.expenseButton} ${styles.expenseButtonSave}`}
              >
                {isExpenseCategoriesSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataEntryValuesPage; 